import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useProducts } from '../context/ProductContext';
import { useCart } from '../context/CartContext';
import { StarIcon, ShieldIcon, TruckIcon, HeartIcon, ShoppingCartIcon, CheckIcon, ChevronRightIcon } from 'lucide-react';
export const ProductDetail: React.FC = () => {
  const {
    productId
  } = useParams<{
    productId: string;
  }>();
  const {
    getProductById
  } = useProducts();
  const {
    addToCart
  } = useCart();
  const product = getProductById(productId || '');
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  if (!product) {
    return <div className="container mx-auto px-4 py-16 text-center" data-id="element-1278">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-1279">
          Product Not Found
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-8" data-id="element-1280">
          The product you're looking for doesn't exist or has been removed.
        </p>
        <Link to="/" className="inline-flex items-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-600" data-id="element-1281">
          Return to Home
        </Link>
      </div>;
  }
  const handleAddToCart = () => {
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image
    });
  };
  return <div className="container mx-auto px-4 py-8" data-id="element-1282">
      {/* Breadcrumbs */}
      <nav className="flex mb-8 text-sm" data-id="element-1283">
        <ol className="flex items-center space-x-2" data-id="element-1284">
          <li data-id="element-1285">
            <Link to="/" className="text-gray-500 dark:text-gray-400 hover:text-yellow-500 dark:hover:text-yellow-500" data-id="element-1286">
              Home
            </Link>
          </li>
          <li className="flex items-center" data-id="element-1287">
            <ChevronRightIcon size={14} className="text-gray-500 dark:text-gray-400" data-id="element-1288" />
          </li>
          <li data-id="element-1289">
            <Link to={`/category/${product.category}`} className="text-gray-500 dark:text-gray-400 hover:text-yellow-500 dark:hover:text-yellow-500" data-id="element-1290">
              {product.category.charAt(0).toUpperCase() + product.category.slice(1)}
            </Link>
          </li>
          <li className="flex items-center" data-id="element-1291">
            <ChevronRightIcon size={14} className="text-gray-500 dark:text-gray-400" data-id="element-1292" />
          </li>
          <li data-id="element-1293">
            <span className="text-gray-900 dark:text-white font-medium" data-id="element-1294">
              {product.name}
            </span>
          </li>
        </ol>
      </nav>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12" data-id="element-1295">
        {/* Product Images */}
        <div data-id="element-1296">
          <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden mb-4" data-id="element-1297">
            <img src={product.images?.[selectedImage] || product.image} alt={product.name} className="w-full h-auto object-contain aspect-square" data-id="element-1298" />
          </div>
          {product.images && product.images.length > 1 && <div className="grid grid-cols-5 gap-2" data-id="element-1299">
              {product.images.map((image, index) => <button key={index} onClick={() => setSelectedImage(index)} className={`border-2 rounded-md overflow-hidden ${selectedImage === index ? 'border-yellow-500' : 'border-gray-200 dark:border-gray-700'}`} data-id="element-1300">
                  <img src={image} alt={`${product.name} view ${index + 1}`} className="w-full h-auto" data-id="element-1301" />
                </button>)}
            </div>}
        </div>
        {/* Product Info */}
        <div data-id="element-1302">
          {product.discount && <span className="inline-block bg-red-500 text-white text-xs font-bold px-2 py-1 rounded mb-4" data-id="element-1303">
              {product.discount}% OFF
            </span>}
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-1304">
            {product.name}
          </h1>
          {product.rating && <div className="flex items-center mb-4" data-id="element-1305">
              <div className="flex" data-id="element-1306">
                {Array.from({
              length: 5
            }).map((_, index) => <StarIcon key={index} size={18} className={index < Math.floor(product.rating) ? 'text-yellow-500 fill-yellow-500' : index < product.rating ? 'text-yellow-500 fill-yellow-500 opacity-50' : 'text-gray-300 dark:text-gray-600'} data-id="element-1307" />)}
              </div>
              <span className="ml-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-1308">
                {product.rating} ({product.reviewCount} reviews)
              </span>
            </div>}
          <div className="flex items-end mb-6" data-id="element-1309">
            <span className="text-3xl font-bold text-gray-900 dark:text-white" data-id="element-1310">
              ${product.price.toLocaleString()}
            </span>
            {product.originalPrice && <span className="ml-3 text-lg text-gray-500 dark:text-gray-400 line-through" data-id="element-1311">
                ${product.originalPrice.toLocaleString()}
              </span>}
          </div>
          <div className="mb-8" data-id="element-1312">
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed" data-id="element-1313">
              {product.description}
            </p>
          </div>
          {/* Quantity Selector */}
          <div className="mb-6" data-id="element-1314">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1315">
              Quantity
            </label>
            <div className="flex items-center" data-id="element-1316">
              <button onClick={() => setQuantity(prev => Math.max(1, prev - 1))} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300" data-id="element-1317">
                -
              </button>
              <input type="number" min="1" value={quantity} onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))} className="w-16 text-center border-t border-b border-gray-300 dark:border-gray-600 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none" data-id="element-1318" />
              <button onClick={() => setQuantity(prev => prev + 1)} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-r-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300" data-id="element-1319">
                +
              </button>
            </div>
          </div>
          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8" data-id="element-1320">
            <button onClick={handleAddToCart} className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white py-3 px-6 rounded-md font-medium flex items-center justify-center" data-id="element-1321">
              <ShoppingCartIcon size={18} className="mr-2" data-id="element-1322" />
              Add to Cart
            </button>
            <button className="flex-1 bg-gray-900 dark:bg-gray-700 hover:bg-gray-800 dark:hover:bg-gray-600 text-white py-3 px-6 rounded-md font-medium flex items-center justify-center" data-id="element-1323">
              Buy Now
            </button>
            <button className="sm:flex-none bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 py-3 px-4 rounded-md flex items-center justify-center" data-id="element-1324">
              <HeartIcon size={18} data-id="element-1325" />
            </button>
          </div>
          {/* Product Guarantees */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-8" data-id="element-1326">
            <div className="flex items-start mb-3" data-id="element-1327">
              <div className="flex-shrink-0 bg-green-100 dark:bg-green-900 p-1 rounded-full mr-3" data-id="element-1328">
                <ShieldIcon size={16} className="text-green-600 dark:text-green-400" data-id="element-1329" />
              </div>
              <div data-id="element-1330">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1331">
                  90-Day Warranty
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-1332">
                  All products come with our satisfaction guarantee
                </p>
              </div>
            </div>
            <div className="flex items-start mb-3" data-id="element-1333">
              <div className="flex-shrink-0 bg-blue-100 dark:bg-blue-900 p-1 rounded-full mr-3" data-id="element-1334">
                <TruckIcon size={16} className="text-blue-600 dark:text-blue-400" data-id="element-1335" />
              </div>
              <div data-id="element-1336">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1337">
                  Free Shipping
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-1338">
                  On orders over $100. Secure payment guaranteed.
                </p>
              </div>
            </div>
          </div>
          {/* Technical Specifications */}
          {product.specs && <div data-id="element-1339">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4" data-id="element-1340">
                Technical Specifications
              </h3>
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-1341">
                <table className="w-full" data-id="element-1342">
                  <tbody data-id="element-1343">
                    {Object.entries(product.specs).map(([key, value], index) => <tr key={key} className={index % 2 === 0 ? 'bg-gray-50 dark:bg-gray-900' : ''} data-id="element-1344">
                          <td className="px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-1345">
                            {key}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white" data-id="element-1346">
                            {value}
                          </td>
                        </tr>)}
                  </tbody>
                </table>
              </div>
            </div>}
        </div>
      </div>
    </div>;
};